import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  Auth,
} from 'firebase/auth';
import { db, auth, secondaryAuth } from '../firebase/firebase';

// Reference to your Firestore collection for delivery partners
const deliveryPartnerCollection = collection(db, "deliveryPartner");

// Create: add a new delivery partner document
export async function createDeliveryPartner(partnerData: any) {
  try {
    const docRef = await addDoc(deliveryPartnerCollection, partnerData);
    console.log("DeliveryPartner created with ID: ", docRef.id);
    return docRef.id;
  } catch (e) {
    console.error("Error adding delivery partner: ", e);
    throw e;
  }
}

// Read: get all delivery partners
export async function getDeliveryPartners() {
  const snapshot = await getDocs(deliveryPartnerCollection);
  // Use docId for the Firestore document ID — avoids collision with the
  // numeric `id` field that exists inside partner documents.
  return snapshot.docs.map(d => ({ docId: d.id, ...d.data() }));
}

// Get partner by Firebase UID
export async function getDeliveryPartnerByUid(uid: string) {
  const q = query(deliveryPartnerCollection, where("uid", "==", uid));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const d = snapshot.docs[0];
  return { docId: d.id, ...d.data() };
}

// Update: update a delivery partner document by ID
export async function updateDeliveryPartner(id: string, updateData: any) {
  const partnerDoc = doc(db, "deliveryPartner", id);
  try {
    await updateDoc(partnerDoc, updateData);
    console.log("DeliveryPartner updated");
  } catch (e) {
    console.error("Error updating delivery partner: ", e);
    throw e;
  }
}

// Delete: delete a delivery partner document by ID
export async function deleteDeliveryPartner(id: string) {
  const partnerDoc = doc(db, "deliveryPartner", id);
  try {
    await deleteDoc(partnerDoc);
    console.log("DeliveryPartner deleted");
  } catch (e) {
    console.error("Error deleting delivery partner: ", e);
    throw e;
  }
}

// Register new delivery partner with email/password
export async function registerDeliveryPartner(
  email: string,
  password: string,
  profileData: {
    storename: string;
    phone: string;
  }
): Promise<string> {
  try {
    // Create Firebase Auth user via secondary app so the admin session is preserved
    const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    const uid = userCredential.user.uid;
    // Sign out of secondary instance immediately — only the UID is needed
    await signOut(secondaryAuth);

    // Create partner document in Firestore
    const partnerData = {
      uid,
      email,
      storename: profileData.storename,
      phone: profileData.phone,
      createdTimestamp: new Date().toISOString(),
      doj: new Date().toISOString(),
      active: true,
      available: false,
      online: false,
      PartnerDl: '',
      image: null,
      partnerLat: 0,
      partnerLon: 0,
      pincode: '',
      storeid: '',
      wallet: 0,
    };

    await addDoc(deliveryPartnerCollection, partnerData);
    console.log("Delivery partner registered with UID: ", uid);
    return uid;
  } catch (e) {
    console.error("Error registering delivery partner: ", e);
    throw e;
  }
}

// Login delivery partner with email/password
export async function loginDeliveryPartner(
  email: string,
  password: string
): Promise<any> {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const uid = userCredential.user.uid;

    // Get partner data from Firestore
    const partner = await getDeliveryPartnerByUid(uid);
    if (!partner) {
      throw new Error("Partner record not found in database");
    }
    return partner;
  } catch (e) {
    console.error("Error logging in delivery partner: ", e);
    throw e;
  }
}

// Logout delivery partner
export async function logoutDeliveryPartner(): Promise<void> {
  try {
    await signOut(auth);
    console.log("Delivery partner logged out");
  } catch (e) {
    console.error("Error logging out: ", e);
    throw e;
  }
}

// Update partner profile (name, phone, etc.)
export async function updatePartnerProfile(
  uid: string,
  updateData: {
    storename?: string;
    phone?: string;
    PartnerDl?: string;
    image?: string;
    partnerLat?: number;
    partnerLon?: number;
    pincode?: string;
    storeid?: string;
    available?: boolean;
    online?: boolean;
    active?: boolean;
  }
): Promise<void> {
  try {
    const partner = await getDeliveryPartnerByUid(uid);
    if (partner) {
      await updateDeliveryPartner((partner as any).docId, updateData);
    }
  } catch (e) {
    console.error("Error updating partner profile: ", e);
    throw e;
  }
}

// Verify partner DL number
export async function verifyPartnerDl(
  uid: string,
  dlNumber: string
): Promise<boolean> {
  // You can implement actual DL verification API here
  // For now, just store it
  try {
    await updatePartnerProfile(uid, { PartnerDl: dlNumber });
    return true;
  } catch (e) {
    console.error("Error verifying DL: ", e);
    return false;
  }
}

// Update partner location
export async function updatePartnerLocation(
  uid: string,
  lat: number,
  lon: number
): Promise<void> {
  try {
    await updatePartnerProfile(uid, {
      partnerLat: lat,
      partnerLon: lon,
      online: true
    });
  } catch (e) {
    console.error("Error updating location: ", e);
    throw e;
  }
}

// Set partner availability
export async function setPartnerAvailability(
  uid: string,
  available: boolean
): Promise<void> {
  try {
    await updatePartnerProfile(uid, { available });
  } catch (e) {
    console.error("Error setting availability: ", e);
    throw e;
  }
}